import React, { forwardRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import { ImageNotSupported } from "@mui/icons-material";
import { PriceListProduct } from "../../../services/toolsService";
import { API_CONFIG } from "../../../config/api";
import { formatCurrencyBDT } from "../../../utils/currency";
import { RECEIPT_CONFIG } from "../../../config/receipt";

interface ProductCardProps {
    product: PriceListProduct;
}

// Rendered at a modest on-screen size and rasterized at a higher scale on
// export (see exportCardImage.ts) — the DOM itself stays small and fast to
// lay out, the exported PNG comes out crisp regardless.
const CARD_WIDTH = 300;

/**
 * A shareable "product card" — photo, name, price, shop branding — meant
 * to be rasterized to PNG (via html2canvas, see exportCardImage.ts) and
 * sent directly to a customer. Pure display component: no data fetching,
 * no export logic, so it can be dropped into a preview grid or an
 * off-screen export batch identically. Forwards its ref to the outer
 * card element, which is exactly what html2canvas needs to capture it.
 *
 * The photo comes from GET /products/{id}/card-image rather than the
 * product's own R2 image URL — that endpoint exists specifically so this
 * card's image can be exported at all (R2 sends no CORS headers, so a
 * canvas can display that image but not read pixels back out of itself
 * to produce a PNG; see product_images.py's get_product_card_image).
 */
export const ProductCard = forwardRef<HTMLDivElement, ProductCardProps>(
    ({ product }, ref) => {
        const [imageFailed, setImageFailed] = useState(false);
        const imageUrl = `${API_CONFIG.BASE_URL}/products/${product.product_id}/card-image`;

        return (
            <Box
                ref={ref}
                sx={{
                    width: CARD_WIDTH,
                    bgcolor: "#fff",
                    borderRadius: 2,
                    overflow: "hidden",
                    border: "1px solid",
                    borderColor: "divider",
                    fontFamily: "system-ui, -apple-system, sans-serif",
                }}
            >
                <Box
                    sx={{
                        width: "100%",
                        bgcolor: "grey.100",
                        position: "relative",
                        // Only the no-photo fallback needs a fixed box to
                        // center its icon in — there's no natural size to
                        // fall back on there. A real photo instead renders
                        // at its own aspect ratio (see the img below): full
                        // card width, height however tall that makes it, so
                        // nothing is ever cropped or stretched to fit a
                        // square that doesn't match the source image.
                        ...(imageFailed && {
                            aspectRatio: "1 / 1",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }),
                    }}
                >
                    {imageFailed ? (
                        <ImageNotSupported sx={{ fontSize: 48, color: "grey.400" }} />
                    ) : (
                        <img
                            src={imageUrl}
                            alt={product.name}
                            crossOrigin="anonymous"
                            onError={() => setImageFailed(true)}
                            style={{ width: "100%", height: "auto", display: "block" }}
                        />
                    )}

                    {/* No solid backdrop chip — logo192.png is already
                        transparent around the cat silhouette, so a filled
                        box behind it would only add an unwanted square.
                        The soft glow comes from box-shadow + border-radius
                        on a wrapper exactly the image's own size (not
                        drop-shadow — html2canvas 1.4.1 silently ignores the
                        `filter` property, verified empirically: a filtered
                        vs. unfiltered render came out byte-identical, while
                        box-shadow/border-radius produced a real diff). The
                        wrapper isn't clipped (no overflow: hidden), so the
                        logo itself still renders at full, undistorted size;
                        only the shadow it casts is circular. */}
                    <Box
                        sx={{
                            position: "absolute",
                            top: 8,
                            right: 8,
                            width: 48,
                            height: 48,
                            borderRadius: "50%",
                            boxShadow:
                                "0 0 8px 4px rgba(255,255,255,0.85), 0 1px 3px rgba(0,0,0,0.4)",
                        }}
                    >
                        <img
                            src="/images/logo192.png"
                            alt=""
                            style={{
                                width: 48,
                                height: 48,
                                objectFit: "contain",
                                display: "block",
                            }}
                        />
                    </Box>

                    <Box
                        sx={{
                            position: "absolute",
                            left: 0,
                            right: 0,
                            bottom: 0,
                            pt: 3,
                            pb: 1,
                            px: 1.5,
                            display: "flex",
                            alignItems: "flex-end",
                            justifyContent: "space-between",
                            gap: 1,
                            background:
                                "linear-gradient(to top, rgba(0,0,0,0.78), rgba(0,0,0,0) 100%)",
                        }}
                    >
                        <Box sx={{ minWidth: 0 }}>
                            <Typography
                                variant="caption"
                                display="block"
                                fontWeight={700}
                                sx={{ color: "#fff", lineHeight: 1.3 }}
                            >
                                {RECEIPT_CONFIG.SHOP_INFO.NAME}
                            </Typography>
                            <Typography
                                variant="caption"
                                display="block"
                                sx={{ color: "rgba(255,255,255,0.9)", lineHeight: 1.3 }}
                            >
                                {RECEIPT_CONFIG.SHOP_INFO.PHONE}
                            </Typography>
                        </Box>

                        {/* A solid white tag rather than colored text —
                            primary.main (#18383E) is nearly as dark as the
                            scrim itself, so plain text here would barely
                            read. The tag guarantees contrast regardless. */}
                        <Box
                            sx={{
                                flexShrink: 0,
                                bgcolor: "#fff",
                                borderRadius: 5,
                                px: 1.25,
                                py: 0.5,
                                boxShadow: "0 1px 4px rgba(0,0,0,0.3)",
                            }}
                        >
                            <Typography
                                variant="subtitle2"
                                fontWeight={800}
                                color="primary.main"
                                sx={{ whiteSpace: "nowrap", lineHeight: 1.2 }}
                            >
                                {formatCurrencyBDT(product.price)}
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                <Box sx={{ p: 2 }}>
                    {/* No line clamp — a truncated product name is worse
                        than a taller card, and the card's whole point is to
                        be sent to a customer showing the real name. */}
                    <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.3 }}>
                        {product.name}
                    </Typography>
                </Box>
            </Box>
        );
    },
);

ProductCard.displayName = "ProductCard";
